import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { dealBuilderApi } from '../api';
import { useDealBuilderContext } from '../context/DealBuilderContext';
import { collectVariablesForCreateProposal } from 'modules/desking';
import { FinanceProgramFormValues } from 'modules/desking';
import { DeskingSubmitOptions } from 'modules/desking/types';

export const useDeskingFormSync = (
  debouncedFormValues: FinanceProgramFormValues | null,
  dealBuilderDeskingData: Pick<
    DeskingSubmitOptions,
    'builder' | 'pricing' | 'productsConfigInMenu' | 'commonProductsConfiguration'
  > & { isProposalDataLoaded: boolean },
  hasSavedDeskingData: boolean
) => {
  const {
    sourceData: { vo },
    activeEntity,
    setActiveEntity,
  } = useDealBuilderContext();
  const [updateProposal] = useMutation(dealBuilderApi.updateProposal);

  // Ref prevents activeEntity updates (from onCompleted) from re-triggering the effect
  const activeEntityRef = useRef(activeEntity);
  activeEntityRef.current = activeEntity;

  useEffect(() => {
    const activeEntity = activeEntityRef.current;
    if (
      !debouncedFormValues ||
      (hasSavedDeskingData && !dealBuilderDeskingData.isProposalDataLoaded) ||
      !activeEntity?.proposalId ||
      !vo?.vendorOpportunityId
    )
      return;

    const { input } = collectVariablesForCreateProposal({
      isDraft: true,
      pricing: dealBuilderDeskingData.pricing,
      formData: debouncedFormValues,
      menuByTerms: dealBuilderDeskingData.builder,
      productsConfigInMenu: dealBuilderDeskingData.productsConfigInMenu,
      commonProductsConfiguration: dealBuilderDeskingData.commonProductsConfiguration,
      voId: vo.vendorOpportunityId,
    });

    updateProposal({
      variables: {
        id: activeEntity.proposalId,
        fieldsObj: {
          saleType: input.saleType,
          settings: input.settings,
        },
      },
      onCompleted: () =>
        setActiveEntity({ ...activeEntity, proposal: { ...activeEntity.proposal, settings: input.settings } }),
    });
  }, [debouncedFormValues, dealBuilderDeskingData, vo, updateProposal, setActiveEntity]);
};
